import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { ArrowRight, ChevronDown, ChevronUp, Wrench, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Breadcrumb, breadcrumbJsonLd } from '../../components/Breadcrumb';

export default function LandingPage() {
  const { citySlug, serviceSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    publicAPI.getLanding(citySlug, serviceSlug)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.status === 404 ? 'Sayfa bulunamadi' : 'Bir hata olustu'))
      .finally(() => setLoading(false));
  }, [citySlug, serviceSlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(221,83%,22%)]"></div></div>;
  
  if (error || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <h1 className="text-2xl font-semibold mb-4">{error || 'Sayfa bulunamadi'}</h1>
      <p className="text-muted-foreground mb-6">Bu sehir ve hizmet icin henuz landing page olusturulmamis.</p>
      <Link to="/teklif-al"><Button>Genel Teklif Formu</Button></Link>
    </div>
  );

  const { city, service, landing, related_services = [], related_cities = [] } = data;
  const faq = landing.faq || [];
  const sections = landing.content_sections || {};

  const breadcrumbItems = [
    { label: 'Ana Sayfa', href: '/' },
    { label: city.name, href: `/${citySlug}` },
    { label: service.name, href: `/${citySlug}/${serviceSlug}` }
  ];

  return (
    <>
      <Helmet>
        <title>{landing.meta_title || (service.name && city.name ? `${service.name} ${city.name} | Teklif Al` : 'Uretim Noktasi')}</title>
        <meta name="description" content={landing.meta_description || `${city.name} ilinde ${service.name} hizmeti icin hemen teklif alin.`} />
        <meta property="og:title" content={landing.meta_title || `${service.name} ${city.name}`} />
        <meta property="og:description" content={landing.meta_description || ''} />
        <link rel="canonical" href={`/${citySlug}/${serviceSlug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": `${service.name} - ${city.name}`,
          "description": landing.meta_description,
          "areaServed": { "@type": "City", "name": city.name },
          "provider": { "@type": "Organization", "name": "Industrial Lead Hub" }
        })}</script>
        {faq.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faq.map(f => ({
              "@type": "Question",
              "name": f.question,
              "acceptedAnswer": { "@type": "Answer", "text": f.answer }
            }))
          })}</script>
        )}
        <script type="application/ld+json">{breadcrumbJsonLd(breadcrumbItems)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[hsl(221,83%,22%)] text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} variant="light" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="landing-h1">
            {landing.h1 || `${service.name} - ${city.name}`}
          </h1>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl">{landing.meta_description}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/teklif-al">
              <Button size="lg" className="bg-white text-[hsl(221,83%,22%)] hover:bg-blue-50 font-semibold" data-testid="landing-cta-teklif">
                Teklif Al <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Content */}
      {landing.intro_html && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: landing.intro_html }} />
        </section>
      )}

      {/* Content Sections */}
      {(sections.service_explanation || sections.city_context) && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            {sections.service_explanation && (
              <div>
                <h2 className="text-xl font-semibold mb-3">{service.name} Hizmeti Hakkinda</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{sections.service_explanation}</p>
              </div>
            )}
            {sections.city_context && (
              <div>
                <h2 className="text-xl font-semibold mb-3">{city.name} Endustriyel Altyapi</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{sections.city_context}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Quick Form Link */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card className="bg-white">
            <CardContent className="p-8">
              <Wrench className="h-12 w-12 text-[hsl(221,83%,22%)] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-3">
                {sections.cta_text || `${city.name} - ${service.name} Teklif Al`}
              </h2>
              <p className="text-muted-foreground mb-6">Teknik cizimlerinizi yukleyin, detaylari belirtin. En uygun firmayi bulalim.</p>
              <Link to="/teklif-al">
                <Button size="lg" className="font-semibold px-8" data-testid="landing-form-cta">
                  Teklif Formu <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold mb-8">Sik Sorulan Sorular</h2>
            <div className="space-y-3">
              {faq.map((item, i) => (
                <div key={i} className="border rounded-lg bg-white" data-testid={`faq-${i}`}>
                  <button
                    className="w-full flex items-center justify-between p-4 text-left font-medium"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    {item.question}
                    {openFaq === i ? <ChevronUp className="h-5 w-5 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 flex-shrink-0" />}
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Internal Linking: Related Services in Same City ─── */}
      {related_services.length > 0 && (
        <section className="py-12 bg-white" data-testid="related-services-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold mb-6">{city.name} - Diger Hizmetler</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related_services.map((rs, i) => (
                <Link
                  key={i}
                  to={rs.url}
                  className="group p-5 bg-slate-50 rounded-xl border hover:border-[hsl(221,83%,22%)] hover:shadow-sm transition-all"
                  data-testid={`related-service-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <Wrench className="h-5 w-5 text-[hsl(221,83%,22%)] mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-sm group-hover:text-[hsl(221,83%,22%)] transition-colors">
                        {rs.anchor_text}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {rs.city_name} ilinde {rs.service_name} hizmeti icin teklif alin
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-xs font-medium text-[hsl(221,83%,22%)] opacity-0 group-hover:opacity-100 transition-opacity">
                    Sayfaya git <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Internal Linking: Same Service in Other Cities ─── */}
      {related_cities.length > 0 && (
        <section className="py-12 bg-slate-50" data-testid="related-cities-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold mb-6">{service.name} - Diger Sehirler</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related_cities.map((rc, i) => (
                <Link
                  key={i}
                  to={rc.url}
                  className="group flex items-center gap-3 p-4 bg-white rounded-xl border hover:border-[hsl(221,83%,22%)] hover:shadow-sm transition-all"
                  data-testid={`related-city-${i}`}
                >
                  <MapPin className="h-5 w-5 text-[hsl(221,83%,22%)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm group-hover:text-[hsl(221,83%,22%)] transition-colors">
                      {rc.anchor_text}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{rc.city_name} ili</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-[hsl(221,83%,22%)] flex-shrink-0 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 border-t bg-white/95 backdrop-blur p-3 lg:hidden">
        <Link to="/teklif-al" className="block">
          <Button className="w-full font-semibold" size="lg" data-testid="mobile-cta">
            Teklif Al <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>
    </>
  );
}
